import {
  TextInput,
  AlertBanner,
  AutoCompleteInput,
  CustomButton,
  CustomSelect,
} from '@/shared/ui/components';
import { withModalLayout } from '@/shared/lib';
import { t } from 'ttag';
import { CONTACT_KEY } from '@/entities/rma';
import { GOOGLE_API_KEY } from '@/shared/config';
import { useEditShipmentModal } from './useEditShipmentModal';
import type { EditShipmentModalProps } from './editShipmentModal.types';
import './editShipmentModal.scss';

/**
 * Modal form for editing the sender or recipient contact of a shipping label. Delegates state, validation and saving to useEditShipmentModal, shows an info banner whose wording depends on isSender and isRmaShippingLabel, renders name, company, country, address, city, state, zip, phone and email inputs bound to that state with per-field error messages, and commits through the Apply button.
 *
 * @param isSender - Whether the edited contact is the shipment sender rather than the recipient.
 * @param data - The contact record used to prefill the form.
 * @param updateData - Callback receiving the edited contact so the parent can persist it.
 * @param listStatesUS - Selectable US state options.
 * @param listStatesCA - Selectable Canadian province options.
 * @param listCountries - Selectable country options.
 * @param isRmaShippingLabel - Whether the label belongs to an RMA, which switches the sender banner text to facility wording instead of patient wording.
 * @returns The edit shipment modal element.
 */
const EditShipmentModal = withModalLayout(
  ({
    isSender = false,
    data,
    updateData,
    listStatesUS,
    listStatesCA,
    listCountries,
    isRmaShippingLabel = false,
  }: EditShipmentModalProps) => {
    const {
      state,
      handleKeyDown,
      onChangeCountry,
      onChangeAddress,
      onChangeCity,
      clearAutoComplete,
      onChange,
      handleClickSave,
      shouldFormatPhoneNumber,
      countriesObject,
      stateObject,
    } = useEditShipmentModal({ data, updateData, listStatesUS, listStatesCA, listCountries });

    return (
      <div className="edit-shipment-modal">
        <div className="custom-modal-body-container">
          <div className="part-label">
            <AlertBanner
              alertType="info"
              description={
                isSender
                  ? isRmaShippingLabel
                    ? t`The sender information is automatically populated based on facility information. Modifying this information does not impact the original data.`
                    : t`The sender information is automatically populated based on patient information. Modifying this information does not impact the original data.`
                  : t`The recipient information is automatically populated based on facility information. Modifying this information does not impact the original data.`
              }
            />
          </div>

          <div className="part-input">
            <TextInput
              onKeyDown={handleKeyDown}
              name={CONTACT_KEY.name}
              id={CONTACT_KEY.name}
              label={t`Name`}
              placeholder={t`Enter name`}
              value={state[CONTACT_KEY.name]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.name]}
              errorMessage={state.errorObject[CONTACT_KEY.name]}
              disabled={state.isSaving}
            />

            <TextInput
              onKeyDown={handleKeyDown}
              name={CONTACT_KEY.company}
              id={CONTACT_KEY.company}
              label={
                <div className="label-with-sub">
                  {t`Company`} <span>{t`(optional)`}</span>
                </div>
              }
              placeholder={t`Enter company`}
              value={state[CONTACT_KEY.company]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.company]}
              errorMessage={state.errorObject[CONTACT_KEY.company]}
              disabled={state.isSaving}
            />

            <CustomSelect
              name={CONTACT_KEY.country}
              id={CONTACT_KEY.country}
              onKeyDown={handleKeyDown}
              title={t`Country`}
              aria-label={t`Country`}
              options={listCountries}
              value={countriesObject}
              onChange={onChangeCountry}
              placeholder="Enter country"
              isError={!!state.errorObject[CONTACT_KEY.country]}
              errorMessage={state.errorObject[CONTACT_KEY.country]}
              disabled={state.isSaving}
              size="middle"
              allowClear={false}
            />

            <AutoCompleteInput
              googleApiKey={GOOGLE_API_KEY}
              isSearchCity={false}
              id={CONTACT_KEY.street1}
              onKeyDown={handleKeyDown}
              title={t`Address`}
              placeholder={t`Enter address`}
              country={state[CONTACT_KEY.country]}
              value={state[CONTACT_KEY.street1]}
              onChangeInput={(value) => onChange(CONTACT_KEY.street1, value)}
              onChangeAddress={onChangeAddress}
              eventClear={clearAutoComplete}
              // isNotClearDefault
              isError={!!state.errorObject[CONTACT_KEY.street1]}
              errorMessage={state.errorObject[CONTACT_KEY.street1]}
              disabled={state.isSaving}
              size="small"
            />

            <TextInput
              onKeyDown={handleKeyDown}
              name={CONTACT_KEY.street2}
              id={CONTACT_KEY.street2}
              label={
                <div className="label-with-sub">
                  {t`Secondary address`} <span>{t`(optional)`}</span>
                </div>
              }
              placeholder={t`Enter secondary address`}
              value={state[CONTACT_KEY.street2]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.street2]}
              errorMessage={state.errorObject[CONTACT_KEY.street2]}
              disabled={state.isSaving}
            />

            <AutoCompleteInput
              googleApiKey={GOOGLE_API_KEY}
              isSearchCity
              id={CONTACT_KEY.city}
              eventClear={clearAutoComplete}
              // isNotClearDefault
              onKeyDown={handleKeyDown}
              title={t`City`}
              placeholder={t`Enter city`}
              country={state[CONTACT_KEY.country]}
              value={state[CONTACT_KEY.city]}
              onChangeInput={(value) => onChange(CONTACT_KEY.city, value)}
              onChange={onChangeCity}
              isError={!!state.errorObject[CONTACT_KEY.city]}
              errorMessage={state.errorObject[CONTACT_KEY.city]}
              className="row-city"
              disabled={state.isSaving}
              size="small"
            />

            {shouldFormatPhoneNumber ? (
              <CustomSelect
                onKeyDown={handleKeyDown}
                name={CONTACT_KEY.state}
                id={CONTACT_KEY.state}
                title={t`State`}
                aria-label={t`State`}
                options={state.country === 'US' ? listStatesUS : listStatesCA}
                placeholder={t`Select a state`}
                value={stateObject}
                onChange={(name, options) => {
                  onChange(name, options?.value);
                }}
                isError={!!state.errorObject[CONTACT_KEY.state]}
                errorMessage={state.errorObject[CONTACT_KEY.state]}
                className="row-state"
                disabled={state.isSaving}
                size="middle"
                allowClear={false}
              />
            ) : (
              <TextInput
                onKeyDown={handleKeyDown}
                name={CONTACT_KEY.state}
                id={CONTACT_KEY.state}
                label={t`State`}
                placeholder={t`Enter state`}
                value={state[CONTACT_KEY.state]}
                onChange={onChange}
                isError={!!state.errorObject[CONTACT_KEY.state]}
                errorMessage={state.errorObject[CONTACT_KEY.state]}
                className="row-state"
                disabled={state.isSaving}
              />
            )}

            <TextInput
              name={CONTACT_KEY.zip}
              id={CONTACT_KEY.zip}
              onKeyDown={handleKeyDown}
              label={t`Zip code`}
              placeholder={t`Enter zip code`}
              value={state[CONTACT_KEY.zip]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.zip]}
              errorMessage={state.errorObject[CONTACT_KEY.zip]}
              disabled={state.isSaving}
            />

            <TextInput
              name={CONTACT_KEY.phone}
              id={CONTACT_KEY.phone}
              onKeyDown={handleKeyDown}
              label={t`Phone number`}
              placeholder={t`Enter phone number`}
              isPhoneNumberMask
              value={state[CONTACT_KEY.phone]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.phone]}
              errorMessage={state.errorObject[CONTACT_KEY.phone]}
              disabled={state.isSaving}
            />

            <TextInput
              name={CONTACT_KEY.email}
              id={CONTACT_KEY.email}
              label={t`Email`}
              placeholder={t`Enter email`}
              value={state[CONTACT_KEY.email]}
              onChange={onChange}
              isError={!!state.errorObject[CONTACT_KEY.email]}
              errorMessage={state.errorObject[CONTACT_KEY.email]}
              disabled={state.isSaving}
            />
          </div>
        </div>

        <div className="part-footer">
          <CustomButton
            loading={state.isSaving}
            onClick={handleClickSave}
            id="next-save-btn"
            type="primary"
            style="default"
            size="middle"
          >
            {t`Apply`}
          </CustomButton>
        </div>
      </div>
    );
  },
);

export default EditShipmentModal;
